import {
  QUESTION_STATUS_PRESENTATIONS,
  UNKNOWN_QUESTION_STATUS_PRESENTATION,
  type PresentationTone,
  type QuestionStatus,
} from '@find-my-part/types';

export type MyQuestionStageCode = QuestionStatus | 'UNKNOWN';

export type MyQuestionStageAction =
  | {
      readonly kind: 'detail';
      readonly label: 'View question' | 'Add details';
    }
  | {
      readonly kind: 'browse';
      readonly label: 'Browse answered questions';
    }
  | {
      readonly kind: 'none';
    };

/**
 * A truthful current-state explanation derived only from the status returned by
 * `/my-questions`. It is intentionally not an event timeline.
 */
export interface MyQuestionStage {
  readonly code: MyQuestionStageCode;
  readonly copy: string;
  readonly tone: PresentationTone;
  readonly action: MyQuestionStageAction;
}

/**
 * Customer-facing stage metadata for every server-owned question status.
 *
 * The tone references the canonical shared presentation register so this view
 * cannot silently diverge from the rest of the widget's status vocabulary.
 */
export const MY_QUESTION_STAGES = {
  PENDING_REVIEW: {
    code: 'PENDING_REVIEW',
    copy: 'Waiting for store review.',
    tone: QUESTION_STATUS_PRESENTATIONS.PENDING_REVIEW.tone,
    action: { kind: 'none' },
  },
  OPEN: {
    code: 'OPEN',
    copy: 'Open for customer replies.',
    tone: QUESTION_STATUS_PRESENTATIONS.OPEN.tone,
    action: { kind: 'detail', label: 'View question' },
  },
  IN_PROGRESS: {
    code: 'IN_PROGRESS',
    copy: 'Store staff are working on this question.',
    tone: QUESTION_STATUS_PRESENTATIONS.IN_PROGRESS.tone,
    action: { kind: 'detail', label: 'View question' },
  },
  NEEDS_MORE_INFO: {
    code: 'NEEDS_MORE_INFO',
    copy: 'Store staff need more information to continue.',
    tone: QUESTION_STATUS_PRESENTATIONS.NEEDS_MORE_INFO.tone,
    action: { kind: 'detail', label: 'Add details' },
  },
  RESOLVED: {
    code: 'RESOLVED',
    copy: 'Store staff marked this question resolved.',
    tone: QUESTION_STATUS_PRESENTATIONS.RESOLVED.tone,
    action: { kind: 'detail', label: 'View question' },
  },
  CLOSED_DUPLICATE: {
    code: 'CLOSED_DUPLICATE',
    copy: 'Store staff marked this question as a duplicate.',
    tone: QUESTION_STATUS_PRESENTATIONS.CLOSED_DUPLICATE.tone,
    action: { kind: 'browse', label: 'Browse answered questions' },
  },
  CLOSED_REJECTED: {
    code: 'CLOSED_REJECTED',
    copy: 'This question was closed after review.',
    tone: QUESTION_STATUS_PRESENTATIONS.CLOSED_REJECTED.tone,
    action: { kind: 'none' },
  },
} as const satisfies Readonly<Record<QuestionStatus, MyQuestionStage>>;

export const UNKNOWN_MY_QUESTION_STAGE = {
  code: 'UNKNOWN',
  copy: 'We can\u2019t show the current status right now.',
  tone: UNKNOWN_QUESTION_STATUS_PRESENTATION.tone,
  action: { kind: 'none' },
} as const satisfies MyQuestionStage;

function isKnownQuestionStatus(value: unknown): value is QuestionStatus {
  return (
    typeof value === 'string' && Object.prototype.hasOwnProperty.call(MY_QUESTION_STAGES, value)
  );
}

/** Resolve unchecked API input without coercing or echoing the supplied value. */
export function resolveMyQuestionStage(value: unknown): MyQuestionStage {
  return isKnownQuestionStatus(value) ? MY_QUESTION_STAGES[value] : UNKNOWN_MY_QUESTION_STAGE;
}
